import { addDays, endOfDay, endOfToday, getTime, startOfDay } from 'date-fns';
import { useFormik } from 'formik';
import { useEffect, useRef, useState } from 'react';
import { Range, RangeKeyDict } from 'react-date-range';
import { MdArrowForwardIos } from 'react-icons/md';
import useSWR from 'swr';
import { reportsPerformanceService, scoreReportService } from 'services';
import { useAuth } from 'hooks';
import {
  DateRangePicker,
  Header,
  Heading,
  Main,
  TableFooter,
  Text,
} from 'components';
import { FontSize, FontWeight, routes } from 'commons';
import { ScoreBigNumbers, ScoreResume, ScoreTable } from './components';
import * as S from './styles';

const DEFAULT_PAST_SELECTION = -30;
const SHOW_SCORE = false;

export function ScoreReportPage() {
  const { selectedStore } = useAuth();
  const store = selectedStore.name;

  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  const mainRef = useRef<HTMLDivElement | null>(null);

  const initialRangeValues: Range = {
    startDate: startOfDay(addDays(new Date(), DEFAULT_PAST_SELECTION)),
    endDate: endOfToday(),
  };

  const { values, setFieldValue, submitForm, resetForm } = useFormik({
    initialValues: initialRangeValues,

    onSubmit: () => {
      mutate();
      mutateResume();
    },

    onReset: () => {
      submitForm();
    },
  });

  const params = {
    endDate: getTime(values.endDate!),
    startDate: getTime(values.startDate!),
  };

  const {
    data: performanceResume,
    isLoading: isResumeLoading,
    mutate: mutateResume,
  } = useSWR(
    `performance-resume-${store}`,
    () => {
      const response = reportsPerformanceService.getResume({
        store,
        startDate: params.startDate,
        endDate: params.endDate,
      });
      return response;
    },
    { revalidateOnFocus: false }
  );

  const { data, mutate, isValidating } = useSWR(
    `score-report-${store}`,
    () =>
      scoreReportService.get({
        store,
        currentPage,
        pageSize,
        startDate: params.startDate,
        endDate: params.endDate,
      }),
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateOnMount: false,
    }
  );

  useEffect(() => {
    mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize]);

  function changeDateRange(dateRange: RangeKeyDict) {
    const { startDate, endDate } = dateRange.range1;

    if (!startDate || !endDate) return;

    const start = startOfDay(startDate);
    const end = endOfDay(endDate);

    setFieldValue('startDate', start);
    setFieldValue('endDate', end);
  }

  return (
    <Main>
      <Header />

      <S.Main ref={mainRef}>
        <div>
          <Text>
            Avance de nível melhorando sua performance para ganhar mais
            benefícios.
          </Text>

          <S.DescriptionWrapper>
            <Text>
              Verifique sua performance no período para uma análise detalhada de
              sua performance.
            </Text>

            <S.AnchorStyle href={routes.performance.path} target="_blank">
              <Text>Ver Performance do período</Text>

              <S.Icon>
                <MdArrowForwardIos />
              </S.Icon>
            </S.AnchorStyle>
          </S.DescriptionWrapper>
        </div>

        <ScoreResume showScore={SHOW_SCORE} />

        <S.DatePickerWrapper>
          <div className="title-container">
            <Heading as="h3" size={FontSize.font16} weight={FontWeight.bold}>
              Performance de Atendimento
            </Heading>

            <Text>
              Acompanhe a performance diária de atendimento de pedidos da loja
              nos últimos 30 dias.
            </Text>
          </div>

          <div className="date-container">
            <DateRangePicker
              mainRef={mainRef}
              onChangeRange={changeDateRange}
              onSubmit={submitForm}
              onReset={resetForm}
              values={values}
              options={{ placement: 'bottom-end', scrollIntoView: true }}
            />
          </div>
        </S.DatePickerWrapper>

        <ScoreBigNumbers
          performanceResume={performanceResume}
          isResumeLoading={isResumeLoading}
        />

        {/* TODO waiting for endpoint */}
        {/* <S.ExportWrapper>
          <Button
            disabled={exportLoading}
            onClick={exportLoading ? undefined : exportToExcel}
          >
            {exportLoading ? (
              <S.Icon>
                <Loading small />
              </S.Icon>
            ) : (
              <S.Icon>
                <MdOutlineFileDownload />
              </S.Icon>
            )}
            Exportar em Excel
          </Button>
        </S.ExportWrapper> */}

        <ScoreTable
          showScore={SHOW_SCORE}
          tableData={data?.results}
          isLoading={isValidating}
        />

        <TableFooter
          pagination={data?.pagination}
          changeCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={isValidating}
        />
      </S.Main>
    </Main>
  );
}
